<template>
	<div class="resume-chart">
		<div class="chart-container" ref="container">
			<svg class="svg" :width="container.width" :height="container.height">
				<defs>
					<mask id="mask">
						<rect :x="margin.left - 2" :y="margin.top" :width="padded.width + 5" :height="padded.height" fill="#fff" />
					</mask>
				</defs>
				<g class="data" mask="url(#mask)">
					<g class="bars-default" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" />
					<g class="bars-min" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" />
					<g class="bars-max" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" />
				</g>
				<rect class="rect"></rect>
			</svg>
		</div>
	</div>
</template>

<script>
import * as d3 from '@/utils/d3'
import Readings from '@/utils/readings'
export default {
	props: [
		'first',
		'readings',
		'type'
	],
	data () {
		return {
			container: {
				width: 0,
				height: 0
			},
			margin: {
				left: 30,
				right: 10,
				top: 20,
				bottom: 25
			},
			scaled: {
				x: null,
				y: null
			},
			axis: {
				x: null,
				y: null
			},
			ticking: false
		}
	},
	computed: {
		padded () {
			const width = Math.max(this.container.width - this.margin.left - this.margin.right, 0)
			const height = Math.max(this.container.height - this.margin.top - this.margin.bottom, 0)
			return { width, height }
		},
		data () {
			return this.readings.length ? [...this.readings] : []
		},
		minValue () {
			return d3.min(this.data, d => d.values.last || d.values.value || d.values.diastolic_last || (d.values.fall ? 1 : 0)) || 0
		},
		maxValue () {
			return d3.max(this.data, d => d.values.last || d.values.value || d.values.systolic_last || (d.values.fall ? 1 : 0)) || 100
		},
		isBloodPressure () {
			return this.type === Readings.TYPE_BLOOD_PRESSURE
		},
		valueMarginMinMax () {
			switch (parseInt(this.type)) {
				case Readings.TYPE_TEMPERATURE:
					return { min: 1, max: 1 }
				case Readings.TYPE_BLOOD_SATURATION:
					return { min: 5, max: 0 }
				case Readings.TYPE_FALLS:
					return { min: 1, max: 0.5 }
				default:
					return { min: 5, max: 5 }
			}
		}
	},
	watch: {
		readings () {
			this.onResize()
		}
	},
	methods: {
		onResize () {
			// Scroll optimization with window.requestAnimationFrame
			if (!this.ticking) {
				window.requestAnimationFrame(() => {
					this.container.width = Math.max(this.$refs.container.offsetWidth, 0)
					this.container.height = Math.max(this.$refs.container.offsetHeight, 0)
					this.update()
					this.ticking = false
				})
				this.ticking = true
			}
		},
		updateBloodPressure () {
			// Min (Diastolic)
			const barsMin = d3.select('.resume-chart .bars-min')
				.selectAll('.bar')
				.data(this.data)
			barsMin.enter().append('rect').merge(barsMin)
				.attr('class', 'bar')
				.attr('x', d => { return this.scaled.x(new Date(d.datetime)) - 1 })
				.attr('y', d => { return this.scaled.y(d.values.diastolic_last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - this.scaled.y(d.values.diastolic_last) })
			barsMin.exit().remove()
			// Max (Systolic)
			const barsMax = d3.select('.resume-chart .bars-max')
				.selectAll('.bar')
				.data(this.data)
			barsMax.enter().append('rect').merge(barsMax)
				.attr('class', 'bar')
				.attr('x', d => { return this.scaled.x(new Date(d.datetime)) + 1 })
				.attr('y', d => { return this.scaled.y(d.values.systolic_last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - this.scaled.y(d.values.systolic_last) })
			barsMax.exit().remove()
		},
		updateCaloriesPedometer () {

		},
		updateFalls () {
			const value = 100
			const barsDefault = d3.select('.resume-chart .bars-default')
				.selectAll('.bar')
				.data(this.data)
			barsDefault.enter().append('rect').merge(barsDefault)
				.attr('class', 'bar')
				.attr('x', d => { return this.scaled.x(new Date(d.datetime)) })
				.attr('y', d => { return this.scaled.y(value) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - this.scaled.y(value) })
			barsDefault.exit().remove()
		},
		updateDefault () {
			const barsDefault = d3.select('.resume-chart .bars-default')
				.selectAll('.bar')
				.data(this.data)
			barsDefault.enter().append('rect').merge(barsDefault)
				.attr('class', 'bar')
				.attr('x', d => { return this.scaled.x(new Date(d.datetime)) })
				.attr('y', d => { return this.scaled.y(d.values.last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - this.scaled.y(d.values.last) })
			barsDefault.exit().remove()
		},
		update () {
			const date = new Date(this.$moment(this.first.date).format())
			const min_date = new Date(this.$moment(this.first.date).subtract(12, 'h').format())
			const max_date = new Date(this.$moment(this.first.date).add(12, 'h').format())

			this.scaled.x = d3.scaleTime()
				.domain([min_date, max_date])
				.range([0, this.padded.width])
			
			/*this.scaled.y = d3.scaleLinear()
				.domain([0, 200])
				.range([this.padded.height, 0])*/
			
			this.scaled.y = d3.scaleLinear()
				.domain([this.minValue - this.valueMarginMinMax.min, this.maxValue + this.valueMarginMinMax.max]).nice(3)
				.range([this.padded.height, 0])

			const svg = d3.select('.resume-chart .svg')

			this.axis.x = d3.axisBottom()
				.scale(this.scaled.x)
				.tickSize(0)
				.tickPadding(6)
				.tickFormat(d3.timeFormat('%H'))
			this.axis.y = d3.axisLeft()
				.scale(this.scaled.y)
				.tickSize(-this.padded.width)
				.ticks(3)

			d3.selectAll('.resume-chart .axis').remove()
			svg.append('g')
				.attr('class', 'x axis')
				.attr('transform', `translate(${this.margin.left}, ${this.margin.top + this.padded.height})`)
				.call(this.axis.x)
			svg.append('g')
				.attr('class', 'y axis')
				.attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
				.call(this.axis.y)

			switch (parseInt(this.type)) {
				case Readings.TYPE_BLOOD_PRESSURE:
					this.updateBloodPressure()
					break
				case Readings.TYPE_CALORIES:
				case Readings.TYPE_PEDOMETER:
					this.updateCaloriesPedometer()
					break
				case Readings.TYPE_FALLS:
					this.updateFalls()
					break
				default:
					this.updateDefault()
			}
			// Bring charts to top layer (z index)
			d3.select('.resume-chart .data').raise()
		}
	},
	mounted () {
		window.addEventListener('resize', this.onResize)
		this.onResize()
	},
	beforeDestroy () {
		window.removeEventListener('resize', this.onResize)
	}
}
</script>



// WEBPACK FOOTER //
// src/components/watch/readings/shared/alerts/ResumeChart.vue